import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Modal } from "./ui";
import { FORMATO_RESUMEN } from "../domain/model";
import { sitiosEnCero } from "../domain/contracts";
import type { Cotizacion, ResultadoCalculo } from "../domain/types";
import Icono from "./Icono";

// Reemplaza confirm()/prompt() del navegador, que salen con el estilo del
// sistema operativo y el dominio del sitio encima. Misma idea que el resto de
// las ventanas del cotizador: <dialog> nativo (foco atrapado, Escape, backdrop)
// con las clases de styles.css.

type Confirmacion = {
  titulo: string;
  mensaje?: string;
  confirmar?: string;
  cancelar?: string;
  // Acción destructiva (eliminar, descartar): botón rojo con ícono de
  // papelera y el foco inicial en Cancelar, para que un Enter apurado no
  // borre nada.
  peligro?: boolean;
};
type PeticionTexto = Confirmacion & { etiqueta: string; valor?: string };

type Pendiente =
  | ({ tipo: "confirmar"; resolver: (v: boolean) => void } & Confirmacion)
  | ({ tipo: "texto"; resolver: (v: string | null) => void } & PeticionTexto);

const Contexto = createContext<{
  confirmar: (o: Confirmacion) => Promise<boolean>;
  pedirTexto: (o: PeticionTexto) => Promise<string | null>;
} | null>(null);

export function Dialogos({ children }: { children: ReactNode }) {
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  // Si llegara una segunda petición con una abierta, la anterior se resuelve
  // como cancelada en vez de quedar colgada esperando para siempre.
  const actual = useRef<Pendiente | null>(null);
  const abrir = useCallback((p: Pendiente) => {
    if (actual.current)
      actual.current.tipo === "texto"
        ? actual.current.resolver(null)
        : actual.current.resolver(false);
    actual.current = p;
    setPendiente(p);
  }, []);
  const cerrar = useCallback((valor: boolean | string | null) => {
    const p = actual.current;
    actual.current = null;
    setPendiente(null);
    if (!p) return;
    if (p.tipo === "texto")
      p.resolver(typeof valor === "string" ? valor : null);
    else p.resolver(valor === true);
  }, []);
  const confirmar = useCallback(
    (o: Confirmacion) =>
      new Promise<boolean>((resolver) =>
        abrir({ ...o, tipo: "confirmar", resolver }),
      ),
    [abrir],
  );
  const pedirTexto = useCallback(
    (o: PeticionTexto) =>
      new Promise<string | null>((resolver) =>
        abrir({ ...o, tipo: "texto", resolver }),
      ),
    [abrir],
  );
  return (
    <Contexto.Provider value={{ confirmar, pedirTexto }}>
      {children}
      {pendiente ? (
        <Modal title={pendiente.titulo} onClose={() => cerrar(null)}>
          {pendiente.tipo === "texto" ? (
            <FormularioTexto peticion={pendiente} onCerrar={cerrar} />
          ) : (
            <>
              {pendiente.mensaje ? <p>{pendiente.mensaje}</p> : null}
              <div className="row end dialogo-acciones">
                <button
                  type="button"
                  autoFocus={pendiente.peligro}
                  onClick={() => cerrar(false)}
                >
                  {pendiente.cancelar ?? "Cancelar"}
                </button>
                <button
                  type="button"
                  autoFocus={!pendiente.peligro}
                  className={pendiente.peligro ? "danger solid" : "primary"}
                  onClick={() => cerrar(true)}
                >
                  {pendiente.peligro ? <Icono nombre="eliminar" /> : null}
                  {pendiente.confirmar ?? "Confirmar"}
                </button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </Contexto.Provider>
  );
}

function FormularioTexto({
  peticion,
  onCerrar,
}: {
  peticion: PeticionTexto;
  onCerrar: (v: string | null) => void;
}) {
  const [valor, setValor] = useState(peticion.valor ?? "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onCerrar(valor.trim() === "" ? null : valor.trim());
      }}
    >
      {peticion.mensaje ? <p>{peticion.mensaje}</p> : null}
      <label className="field">
        <span>{peticion.etiqueta}</span>
        <input
          autoFocus
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
      </label>
      <div className="row end dialogo-acciones">
        <button type="button" onClick={() => onCerrar(null)}>
          {peticion.cancelar ?? "Cancelar"}
        </button>
        <button
          type="submit"
          className="primary"
          disabled={valor.trim() === ""}
        >
          <Icono nombre="guardar" />
          {peticion.confirmar ?? "Agregar"}
        </button>
      </div>
    </form>
  );
}

function useDialogos() {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error("Falta <Dialogos> por encima de este componente.");
  return ctx;
}
export const useConfirmar = () => useDialogos().confirmar;
export const usePedirTexto = () => useDialogos().pedirTexto;

// Resumen por tienda y zona: si el cálculo autoritativo dejó tiendas en $0
// (sin intervención en la campaña, típico de Adidas), el vendedor elige si se
// listan con "-" o se omiten del PDF. Otros formatos no preguntan.
export function usePreguntarSitiosEnCero() {
  const confirmar = useConfirmar();
  return useCallback(
    async (c: Cotizacion, r: ResultadoCalculo) => {
      if (c.formatoPdf !== FORMATO_RESUMEN) return true;
      const enCero = sitiosEnCero(r);
      if (!enCero.length) return true;
      const una = enCero.length === 1;
      return confirmar({
        titulo: una
          ? "Hay una tienda en $0"
          : `Hay ${enCero.length} tiendas en $0`,
        mensaje: `${enCero.join(", ")} ${una ? "no tiene" : "no tienen"} valor en esta cotización. ¿${una ? "La incluyes" : "Las incluyes"} en el resumen (con "-" en cada columna) o ${una ? "la omites" : "las omites"} del PDF?`,
        confirmar: "Incluir en $0",
        cancelar: "Omitir del PDF",
      });
    },
    [confirmar],
  );
}
